import { useState, useEffect, useRef, useCallback } from 'react';
import { message } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { aiVideoApi, createAiVideoApi } from '../services/aiVideoApi';
import { CHAT_MESSAGE_TYPES, QUICK_ACTION_MAP } from '../config/constants';
import { useMqtt } from '../context/MqttContext';

// 訊息類型定義
interface ChatMessage {
  id: number;
  user: string;
  message: string;
  time: string;
  type: keyof typeof CHAT_MESSAGE_TYPES; // 只支持 API 定義的格式
  viewer_id?: string; // 用於獲取觀眾詳細信息
}

// 觀眾類型定義
interface Viewer {
  id: string;
  name: string;
  room_code: string;
  created_at: string;
}

/**
 * 直播聊天室 Hook
 * 處理房間管理、觀眾管理和訊息功能
 */
export const useLivestreamChat = (roomCode: string, options: { autoCreateRoom?: boolean; enableQuery?: boolean } = {}) => {
  const { autoCreateRoom = false, enableQuery = false } = options;
  const queryClient = useQueryClient();
  const [currentViewer, setCurrentViewer] = useState<Viewer | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const hasInitialized = useRef(false);
  const viewerCache = useRef<Map<string, string>>(new Map()); // 緩存觀眾名稱

  // MQTT 連接
  const { connectionStatus, subscribe, unsubscribe, publish } = useMqtt();

  // 創建或獲取直播房間
  const { data: roomData, isLoading: isRoomLoading, error: roomError } = useQuery({
    queryKey: ['livestream-room', roomCode],
    queryFn: () => {
      console.log('查詢房間:', roomCode);
      return aiVideoApi.getLivestreamRoom(roomCode);
    },
    enabled: !!roomCode && enableQuery, // 只有在明確啟用查詢時才執行
    retry: false,
    onError: (error: any) => {
      console.log('房間查詢失敗:', error);
      console.log('錯誤詳情:', error.response?.data || error.message);
    }
  });

  // 獲取訊息 - 使用 useQuery (手動觸發模式，不輪詢)
  const {
    data: messagesData,
    isLoading: isMessagesLoading,
    error: messagesError,
    refetch: refetchMessages
  } = useQuery({
    queryKey: ['livestream-messages', roomCode],
    queryFn: async () => {
      console.log('查詢訊息:', roomCode);
      const response = await aiVideoApi.getMessages({ roomCode, limit: 50 });
      console.log('訊息查詢響應:', response);
      return response;
    },
    enabled: false, // 禁用自動查詢，改為手動觸發
    retry: 2,
    staleTime: 0,
    cacheTime: 1000 * 60 * 5,
    onError: (error: any) => {
      console.error('獲取訊息失敗:', error);
    },
    onSuccess: (data: any) => {
      console.log('訊息查詢成功:', data);
    },
    select: (data: any) => {
      console.log('select 函數處理數據:', data);

      // 處理不同的數據結構
      let messages: any[] = [];

      if (Array.isArray(data)) {
        // 如果 data 是數組，過濾出訊息對象（排除 status 等非對象元素）
        messages = data.filter(item => item && typeof item === 'object' && item.id);
        console.log('從數組中提取的訊息:', messages);
      } else if (data && data.messages && Array.isArray(data.messages)) {
        // 如果 data 有 messages 屬性
        messages = data.messages;
        console.log('從 data.messages 提取的訊息:', messages);
      } else if (data && typeof data === 'object' && data.id) {
        // 如果 data 本身就是單個訊息對象
        messages = [data];
        console.log('單個訊息對象:', messages);
      }

      if (messages.length > 0) {
        // 創建 action 值映射函數
        const mapActionToType = (action: string): keyof typeof CHAT_MESSAGE_TYPES => {
          switch (action?.toLowerCase()) {
            case 'like':
              return 'LIKE';
            case 'share':
              return 'SHARE';
            case 'follow':
              return 'FOLLOW';
            case 'message':
              return 'MESSAGE';
            default:
              return 'MESSAGE';
          }
        };

        const formattedMessages: ChatMessage[] = messages.map((msg: any) => ({
          id: msg.id,
          user: '載入中...', // 暫時顯示載入中，稍後會通過 viewer_id 獲取真實名稱
          message: msg.content,
          time: new Date(msg.created_at).toLocaleTimeString('zh-TW', {
            hour: '2-digit',
            minute: '2-digit'
          }),
          type: mapActionToType(msg.action),
          viewer_id: msg.viewer_id // 保存 viewer_id 用於後續查詢
        }));
        console.log('格式化後的訊息（待補充觀眾名稱）:', formattedMessages);
        return formattedMessages.reverse(); // 最新訊息在下方
      }

      console.log('沒有找到訊息數據，返回空數組');
      return [];
    }
  });

  // 當獲取到新訊息時，補充觀眾名稱
  useEffect(() => {
    const fetchViewerNames = async () => {
      if (!messagesData || messagesData.length === 0) {
        setMessages([]);
        return;
      }

      const updatedMessages = [...messagesData];
      let hasUpdates = false;

      for (let i = 0; i < updatedMessages.length; i++) {
        const msg = updatedMessages[i];
        if (msg.viewer_id && msg.user === '載入中...') {
          // 檢查緩存
          if (viewerCache.current.has(msg.viewer_id)) {
            const cachedName = viewerCache.current.get(msg.viewer_id)!;
            updatedMessages[i] = { ...msg, user: cachedName.split('_')[0] };
            hasUpdates = true;
          } else {
            // 獲取觀眾信息
            try {
              const viewerData = await aiVideoApi.getViewer(msg.viewer_id);
              const viewerName = viewerData?.name || '匿名用戶';

              // 緩存觀眾名稱
              viewerCache.current.set(msg.viewer_id, viewerName);

              // 更新訊息
              updatedMessages[i] = { ...msg, user: viewerName.split('_')[0] };
              hasUpdates = true;

              console.log(`獲取觀眾名稱成功: ${msg.viewer_id} -> ${viewerName}`);
            } catch (error) {
              console.error(`獲取觀眾名稱失敗: ${msg.viewer_id}`, error);
              // 設置為匿名用戶
              updatedMessages[i] = { ...msg, user: '匿名用戶' };
              hasUpdates = true;
            }
          }
        }
      }

      if (hasUpdates) {
        setMessages(updatedMessages);
      }
    };

    fetchViewerNames();
  }, [messagesData]);

  // MQTT 訂閱和訊息處理
  useEffect(() => {
    if (!roomCode) return;

    const livestreamTopic = 'eternal/livestream';

    // 訂閱 MQTT 主題
    const success = subscribe(livestreamTopic, (mqttMessage: any) => {
      console.log('收到 MQTT 訊息:', mqttMessage);

      // 檢查是否是當前房間的訊息
      if (mqttMessage && mqttMessage.roomCode === roomCode) {
        console.log('收到當前房間的訊息更新通知，刷新訊息');
        // 收到通知後刷新訊息
        refetchMessages();
      }
    });

    if (success) {
      console.log(`已訂閱 MQTT 主題: ${livestreamTopic}`);
    }

    // 初始載入訊息（如果已連接聊天室）
    if (isConnected) {
      console.log('初始載入訊息');
      refetchMessages();
    }

    return () => { unsubscribe(livestreamTopic); };
  }, [roomCode, isConnected, subscribe, unsubscribe, refetchMessages]);

  // 調試日誌和 MQTT 連接監控
  useEffect(() => {
    console.log('useLivestreamChat - MQTT狀態:', connectionStatus);
    console.log('useLivestreamChat - messagesData:', messagesData);
    console.log('useLivestreamChat - messages (with names):', messages);
    console.log('useLivestreamChat - isMessagesLoading:', isMessagesLoading);
    console.log('useLivestreamChat - messagesError:', messagesError);

    // 監控 MQTT 連接狀態變化
    if (connectionStatus === 'disconnected' || connectionStatus === 'error') {
      console.warn('MQTT 連接異常:', connectionStatus);
    } else if (connectionStatus === 'connected') {
      console.log('MQTT 連接正常');
    }
  }, [connectionStatus, messagesData, messages, isMessagesLoading, messagesError]);

  // 創建房間 mutation
  const createRoomMutation = useMutation({
    mutationFn: ({ livestreamId, livestreamUrl }: { livestreamId: string; livestreamUrl: string }) =>
      aiVideoApi.createLivestreamRoom({ livestreamId, livestreamUrl }),
    onSuccess: (data: any) => {
      console.log('房間創建成功:', data);
      message.success('聊天室已準備就緒');
      // 使房間查詢失效，重新獲取
      queryClient.invalidateQueries({ queryKey: ['livestream-room', roomCode] });
    },
    onError: (error: any) => {
      console.error('創建房間失敗:', error);
      message.error('聊天室創建失敗');
    }
  });

  // 添加觀眾 mutation
  const addViewerMutation = useMutation({
    mutationFn: ({ name, roomCode }: { name: string; roomCode: string }) =>
      aiVideoApi.addViewer({ name, roomCode }),
    onSuccess: (data: Viewer) => {
      setCurrentViewer(data);
      setIsConnected(true);
      console.log('觀眾添加成功:', data);
      // 載入初始訊息
      refetchMessages();
    },
    onError: (error: any) => {
      console.error('添加觀眾失敗:', error);
      message.error('加入聊天室失敗');
    }
  });

  // 發送訊息 mutation
  const sendMessageMutation = useMutation({
    mutationFn: ({ action, content }: { action: string; content: string }) => {
      if (!currentViewer) {
        throw new Error('請先加入聊天室');
      }
      return aiVideoApi.sendMessage({
        roomCode,
        action,
        content,
        viewerId: currentViewer.id
      });
    },
    // 樂觀更新：在 API 調用前立即更新 UI
    onMutate: async ({ action, content }: { action: string; content: string }) => {
      // 取消正在進行的查詢
      await queryClient.cancelQueries({ queryKey: ['livestream-messages', roomCode] });

      // 保存當前狀態（用於回滾）
      const previousMessages = messages;

      // 創建樂觀更新的訊息
      const optimisticMessage: ChatMessage = {
        id: Date.now(), // 使用時間戳作為臨時 ID
        user: currentViewer?.name.split('_')[0] || '您',
        message: content,
        time: new Date().toLocaleTimeString('zh-TW', {
          hour: '2-digit',
          minute: '2-digit'
        }),
        type: action as keyof typeof CHAT_MESSAGE_TYPES,
        viewer_id: currentViewer?.id
      };

      // 立即更新本地狀態
      setMessages(prev => [...prev, optimisticMessage]);

      console.log('樂觀更新：已添加訊息到本地狀態', optimisticMessage);

      // 返回上下文以便在錯誤時回滾
      return { previousMessages, optimisticMessage };
    },
    onSuccess: (data: any, variables: { action: string; content: string }, context: any) => {
      console.log('訊息發送成功');

      // 發送 MQTT 通知其他客戶端刷新訊息
      const mqttNotification = {
        type: 'message_update',
        roomCode: roomCode,
        timestamp: new Date().toISOString()
      };

      const success = publish('eternal/livestream', mqttNotification);
      if (success) {
        console.log('MQTT 通知已發送:', mqttNotification);
      } else {
        console.warn('MQTT 通知發送失敗，手動刷新訊息');
        // 如果 MQTT 發送失敗，手動刷新本地訊息
        refetchMessages();
      }
    },
    onError: (error: any, variables: { action: string; content: string }, context: any) => {
      console.error('發送訊息失敗:', error);

      // 回滾到之前的狀態
      if (context?.previousMessages) {
        setMessages(context.previousMessages);
        console.log('樂觀更新回滾：已恢復之前的訊息狀態');
      }

      message.error('發送訊息失敗');
    },
    onSettled: () => {
      // 無論成功失敗，都重新獲取最新數據確保一致性
      // 延遲一下避免與樂觀更新衝突
      setTimeout(() => {
        refetchMessages();
      }, 1000);
    }
  });

  // 初始化聊天室 - 簡化版
  const initializeChat = useCallback(async (displayName: string) => {
    // 防重複檢查
    if (hasInitialized.current || isConnected || currentViewer) {
      console.log('聊天室已初始化，跳過重複調用');
      return;
    }

    hasInitialized.current = true;

    try {
      console.log('=== 聊天室初始化開始 ===');
      console.log('roomCode:', roomCode);
      console.log('displayName:', displayName);

      // 1. 檢查 memberInfo 中的觀眾信息
      const memberInfo = JSON.parse(localStorage.getItem('memberInfo') || '{}');
      const existingViewer = memberInfo.viewer;

      // 向後兼容：如果現有觀眾沒有 displayName，從 name 中提取
      let existingDisplayName = existingViewer?.displayName;
      if (!existingDisplayName && existingViewer?.name) {
        // 嘗試從 name 中提取 displayName（去掉時間戳）
        const nameParts = existingViewer.name.split('_');
        if (nameParts.length > 1) {
          existingDisplayName = nameParts.slice(0, -1).join('_'); // 去掉最後一個時間戳部分
        } else {
          existingDisplayName = existingViewer.name;
        }
      }

      if (existingViewer?.roomCode === roomCode &&
        existingDisplayName === displayName &&
        existingViewer?.id) {
        console.log('發現現有觀眾信息，房間和顯示名稱都匹配:', existingViewer);
        console.log('existingDisplayName:', existingDisplayName, 'inputDisplayName:', displayName);

        // 直接使用現有觀眾信息
        const viewer: Viewer = {
          id: existingViewer.id,
          name: existingViewer.name,
          room_code: roomCode,
          created_at: new Date().toISOString()
        };
        setCurrentViewer(viewer);
        setIsConnected(true);
        // 載入初始訊息
        refetchMessages();
        console.log('使用現有觀眾信息，直接進入聊天室');
      } else {
        if (existingViewer?.roomCode && existingViewer.roomCode !== roomCode) {
          console.log('房間不匹配，創建新觀眾。當前房間:', roomCode, '存儲房間:', existingViewer.roomCode);
        } else if (existingDisplayName && existingDisplayName !== displayName) {
          console.log('顯示名稱不匹配，創建新觀眾。當前名稱:', displayName, '存儲名稱:', existingDisplayName);
        } else {
          console.log('沒有觀眾信息，創建新觀眾');
        }

        // 生成帶時間戳的完整觀眾名稱
        const timestamp = Date.now();
        const viewerName = `${displayName}_${timestamp}`;
        console.log('生成的完整觀眾名稱:', viewerName);

        // 創建新觀眾
        const newViewer = await addViewerMutation.mutateAsync({
          name: viewerName,
          roomCode: roomCode
        });

        // 將觀眾信息存儲到 memberInfo（包含 displayName）
        memberInfo.viewer = {
          id: newViewer.id,
          name: newViewer.name, // 帶時間戳的完整名稱
          displayName: displayName, // 不帶時間戳的顯示名稱
          roomCode: roomCode
        };
        localStorage.setItem('memberInfo', JSON.stringify(memberInfo));
        console.log('新觀眾創建成功，信息已存儲到 memberInfo:', {
          id: newViewer.id,
          name: newViewer.name,
          displayName: displayName,
          roomCode: roomCode
        });
      }

      // 3. 初始化完成
      console.log('聊天室初始化完成，使用 MQTT 模式');

    } catch (error) {
      console.error('初始化聊天室失敗:', error);
      hasInitialized.current = false;

      let errorMessage = '聊天室初始化失敗';
      if (error instanceof Error) {
        errorMessage = error.message;
      } else if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as any;
        errorMessage = axiosError.response?.data?.message || axiosError.message || errorMessage;
      }

      throw new Error(errorMessage);
    }
  }, [roomCode, addViewerMutation]);

  // 手動獲取訊息
  const fetchMessages = useCallback(() => {
    return refetchMessages();
  }, [refetchMessages]);

  // 發送訊息
  const sendMessage = useCallback((content: string, action: string = CHAT_MESSAGE_TYPES.MESSAGE) => {
    if (!content.trim()) return;

    sendMessageMutation.mutate({
      action,
      content: content.trim()
    });
  }, [sendMessageMutation]);

  // 發送快速動作
  const sendQuickAction = useCallback((actionType: string) => {
    const mapping = QUICK_ACTION_MAP[actionType as keyof typeof QUICK_ACTION_MAP];
    if (mapping) {
      sendMessage(mapping.content, mapping.action);
    } else {
      // 默認使用 MESSAGE
      sendMessage(actionType, CHAT_MESSAGE_TYPES.MESSAGE);
    }
  }, [sendMessage]);

  // MQTT 集成輔助方法
  // 添加新訊息到本地狀態（供 MQTT 使用）
  const addMessage = useCallback((mqttMessage: any) => {
    console.log('收到 MQTT 訊息:', mqttMessage);

    const formattedMessage: ChatMessage = {
      id: mqttMessage.id,
      user: mqttMessage.viewer_name ? mqttMessage.viewer_name.split('_')[0] : '匿名用戶', // MQTT 已包含 viewer_name
      message: mqttMessage.content,
      time: new Date(mqttMessage.timestamp).toLocaleTimeString('zh-TW', {
        hour: '2-digit',
        minute: '2-digit'
      }),
      type: mqttMessage.action || CHAT_MESSAGE_TYPES.MESSAGE,
      viewer_id: mqttMessage.viewer_id
    };

    setMessages(prev => {
      // 檢查是否已存在相同 ID 的訊息，避免重複
      const exists = prev.some(msg => msg.id === formattedMessage.id);
      if (exists) {
        console.log('訊息已存在，跳過添加:', formattedMessage.id);
        return prev;
      }

      console.log('MQTT 新訊息已添加:', formattedMessage);
      return [...prev, formattedMessage];
    });
  }, []);

  // 清理函數
  useEffect(() => {
    return () => {
      hasInitialized.current = false;
    };
  }, []);

  return {
    // 狀態
    messages: messages, // 使用包含觀眾名稱的 messages
    currentViewer,
    isConnected,
    isLoading: isRoomLoading || createRoomMutation.isLoading || addViewerMutation.isLoading || isMessagesLoading,

    // 聊天室方法
    initializeChat,
    sendMessage,
    sendQuickAction,

    // Mutation 狀態
    isSending: sendMessageMutation.isLoading,

    // API 控制（向後兼容）
    fetchMessages,

    // MQTT 集成方法
    addMessage      // 添加 MQTT 收到的新訊息
  };
}; 